<template>
  <div class="bill-pay">
    <div class="bill-pay-container">
      <div v-if="loading" class="loading-container">
        <v-progress-circular class="loading-spinner" indeterminate color="primary" size="48" />
      </div>

      <div v-else class="bill-pay-content">
        <div class="family-list">
          <div v-for="patient in family" :key="patient.id" class="patient-card">
            <div class="patient-header" @click="togglePatient(patient.id)">
              <div class="patient-info">
                <div class="patient-name">{{ patient.firstName }} {{ patient.lastName }} <span class="patient-status">({{ patient.status }})</span></div>
              </div>
              <div class="patient-balance">
                <div class="balance-amount">{{ formatMoney(balanceData.balancesByPatient[patient.id], true) }}</div>
                <v-icon class="expand-icon" :class="{ expanded: expanded.includes(patient.id) }">mdi-chevron-down</v-icon>
              </div>
            </div>

            <div v-if="expanded.includes(patient.id)" class="patient-details">
              <div v-if="!breakdownByFamilyMember[patient.id]" class="no-data">
                No appointments, claims, or payments have been posted for this patient.
              </div>
              <div v-else class="patient-breakdown">
                <div class="appointments-section">
                  <h3 class="section-title">Appointments</h3>
                  <div v-if="breakdownByFamilyMember[patient.id].appointments.length" class="appointments-table">
                    <div class="table-header">
                      <div class="header-cell date-col">Date</div>
                      <div class="header-cell description-col">Description</div>
                      <div class="header-cell amount-col">Fee</div>
                      <div class="header-cell amount-col">Insurance</div>
                      <div class="header-cell amount-col">Write-off</div>
                      <div class="header-cell amount-col">Patient<br>Portion</div>
                    </div>
                    <div v-for="appointment in breakdownByFamilyMember[patient.id].appointments" :key="appointment.appointmentId" 
                         class="table-row" @click="showAppointmentDetails(appointment)">
                      <div class="table-cell date-col" data-label="Date">{{ formatDate(appointment.date) }}</div>
                      <div class="table-cell description-col" data-label="Description">
                        <span v-if="appointment.status === 'Broken'" class="broken-appointment">&lt;Broken Appointment&gt;&nbsp;</span>
                        {{ appointment.description }}
                      </div>
                      <div class="table-cell amount-col" data-label="Fee">{{ formatMoney(appointment.fee) }}</div>
                      <div class="table-cell amount-col" data-label="Insurance">
                        <span class="value-with-indicator" :class="{ 'has-pending': appointment.hasUnresolvedClaims }">
                          {{ formatMoney(appointment.insurancePayment) }}
                        </span>
                      </div>
                      <div class="table-cell amount-col" data-label="Write-off">{{ formatMoney(appointment.writeoff) }}</div>
                      <div class="table-cell amount-col patient-amount" data-label="Patient Portion">
                        <span class="value-with-indicator" :class="{ 'has-pending': appointment.hasUnresolvedClaims }">
                          {{ formatMoney(appointment.balance) }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="empty-state">
                    <div class="empty-state-message">No appointments on record</div>
                  </div>
                </div>

                <div class="payments-section">
                  <h3 class="section-title">Payments</h3>
                  <div v-if="breakdownByFamilyMember[patient.id].payments.length" class="payments-table">
                    <div class="table-header">
                      <div class="header-cell date-col">Date</div>
                      <div class="header-cell description-col">Note</div>
                      <div class="header-cell type-col">Type</div>
                      <div class="header-cell amount-col">Amount</div>
                    </div>
                    <div v-for="payment in breakdownByFamilyMember[patient.id].payments" :key="payment.paymentId" class="table-row payment-row">
                      <div class="table-cell date-col" data-label="Date">{{ formatDate(payment.date) }}</div>
                      <div class="table-cell description-col" data-label="Note">{{ payment.note }}</div>
                      <div class="table-cell type-col" data-label="Type">{{ payment.type }}</div>
                      <div class="table-cell amount-col" data-label="Amount">{{ formatMoney(payment.amount) }}</div>
                    </div>
                  </div>
                  <div v-else class="empty-state">
                    <div class="empty-state-message">No payments on record</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="account-summary">
          <div class="summary-note">*Balance reflects appointments with "completed" insurance claims</div>
          <div class="total-balance">
            <span class="balance-label">Account Balance*:</span>
            <span class="balance-amount">{{ formatMoney(balanceData.balance) }}</span>
          </div>
        </div>

        <div v-if="!isAdminUser" class="payment-actions">
          <v-btn class="btn-primary payment-btn" @click="startPayment()">
            <v-icon left size="18">mdi-credit-card</v-icon>
            Make Payment
          </v-btn>
        </div>
      </div>
    </div>

    <v-dialog v-model="showPaymentDialog" persistent width="400" max-height="400">
      <v-card class="modern-modal">
        <v-card-title class="modal-title">Make a Payment</v-card-title>
        <v-card-text class="modal-content">
          <iframe
            v-if="paymentUrl"
            id="payconnectFrame"
            name="payconnectFrame"
            :src="paymentUrl"
            frameborder="0"
            style="width: 100%"
            height="350"
            scrolling="no">
          </iframe>
        </v-card-text>
        <v-card-actions class="modal-actions">
          <v-btn class="btn-secondary" @click="showPaymentDialog = false">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showReceiptDialog" persistent width="320">
      <v-card class="modern-modal">
        <v-card-title class="modal-title">
          <v-icon color="#10b981" size="24" class="mr-3">mdi-check-circle</v-icon>
          Payment Received
        </v-card-title>
        <v-card-text class="modal-content">
          <p class="modal-question">
            Your payment has been received and applied to your account.
          </p>
        </v-card-text>
        <v-card-actions class="modal-actions">
          <v-btn class="btn-primary" @click="showReceiptDialog = false">
            Okay
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showAppointmentDetailDialog" persistent max-width="500">
      <v-card class="modern-modal modal-fixed-actions">
        <v-card-title class="modal-title">{{ formatDate(selectedAppointment.date) }} Appointment</v-card-title>
        <v-card-text class="modal-content">
          <div class="appointment-details">
            <div class="detail-section">
              <h4 class="detail-title">Provider</h4>
              <div class="detail-text">{{ selectedAppointment.providerFirstName }} {{ selectedAppointment.providerLastName }}</div>
            </div>
            
            <div class="detail-section">
              <h4 class="detail-title">Description</h4>
              <div class="detail-text">{{ selectedAppointment.description }}</div>
            </div>

            <div v-if="selectedAppointment.claims?.length" class="detail-section">
              <h4 class="detail-title">Insurance</h4>
              <div v-for="(item, i) in selectedAppointment.claims" :key="i" class="info-card">
                <div class="info-row"><span class="info-label">Claim:</span> {{ item.claimId }}</div>
                <div class="info-row">
                  <span class="info-label">Status:</span> 
                  <span class="status-badge" :class="{ 'status-complete': item.claimStatus === 'R', 'status-pending': item.claimStatus !== 'R' }">
                    {{ item.claimStatus === 'R' ? 'Complete' : 'Pending' }}
                  </span>
                </div>
                <div v-if="item.reasonUnderpaid" class="info-row"><span class="info-label">Reason underpaid:</span> {{ item.reasonUnderpaid }}</div>
              </div>
            </div>

            <div class="detail-section">
              <h4 class="detail-title">Procedures Performed</h4>
              <div v-for="(item, i) in selectedAppointment.procedures" :key="i" class="info-card">
                <div class="info-row"><span class="info-label">Description:</span> {{ item.description }}</div>
                <div class="info-row"><span class="info-label">Code:</span> {{ item.code }}</div>
                <div class="info-row"><span class="info-label">Fee:</span> {{ formatMoney(item.fee) }}</div>
                <div class="info-row">
                  <span class="info-label">Payment Tracking:</span>
                  <span class="status-badge" :class="{ 'status-complete': item.paymentTracking === 'Complete', 'status-pending': item.paymentTracking !== 'Complete' }">
                    {{ item.paymentTracking }}
                  </span>
                </div>
                <div v-if="item.remarks" class="info-row"><span class="info-label">Remarks:</span> {{ item.remarks }}</div>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="modal-actions">
          <v-btn class="btn-secondary" @click="showAppointmentDetailDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DataService from '../../../_services/DataService';
import Utils from '../../utils.js';

export default {
  name: 'BillPay',
  data() {
    return {
      pageName: 'Bill Pay',
      isAdminUser: false,
      page: 1,
      itemsPerPage: 5,
      loading: true,
      showReceiptDialog: false,
      showPaymentDialog: false,
      showAppointmentDetailDialog: false,
      gridLoadMessage: 'Gathering Bills Data..',
      balanceData: {},
      selectedAppointment: {},
      family: [],
      breakdownByFamilyMember: {},
      formatDate: Utils.formatDate,
      formatMoney: Utils.formatCurrency,
      expanded: [],
      paymentUrl: null,
    };
  },
  methods: {
    togglePatient(patientId) {
      const index = this.expanded.indexOf(patientId);
      if (index > -1) {
        this.expanded.splice(index, 1);
      } else {
        this.expanded.push(patientId);
      }
    },
    goToBilling() {
      this.$router.push({
        name: 'Account',
        query: {activeComponent: 4},
      });
    },
    async startPayment() {
      let patientId;
      if (this.isAdminUser) {
        patientId = this.$route.query.patientId;
      }

      const response = await DataService.getPaymentUrl(patientId);
      if (response.status === 200) {
        this.paymentUrl = response.data.url;
        this.showPaymentDialog = true;
        
        // Listen for payment completion messages
        window.addEventListener('message', this.handlePayConnectMessage);
      } else {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: 'Unable to initialize payment at this time.',
        });
      }
    },
    async handlePayConnectMessage(evt) {
      if (evt.data.status === 'ONLOAD') {
        this.showPaymentDialog = true;
      } else if (evt.data.status === 'SUCCESS') {
        const referenceId = evt.data.response?.referenceId;
        
        const response = await DataService.savePayment({ referenceId });
        if (response.status === 200) {
          await Promise.all([
            this.getBalanceData(),
            this.getBillingBreakdown(),
          ]);
          
          this.showPaymentDialog = false;
          this.paymentUrl = null;
          this.showReceiptDialog = true;
        } else {
          this.$root.$emit('raise_alert', {
            color: 'error',
            text: 'Payment processing failed. Please contact support.',
          });
        }
        
        window.removeEventListener('message', this.handlePayConnectMessage);
      }
    },

    async getPatientFamily() {
      let patientId;
      if (this.isAdminUser) {
        patientId = this.$route.query.patientId;
      }

      const response = await DataService.getPatientFamily(patientId);
      if (response.status === 200) {
        const primary = response.data.find((patient) => patient.status === 'Self');
        const rest = response.data.filter((patient) => patient.status !== 'Self');
        rest.sort((a, b) => a.firstName > b.firstName ? 1 : -1); // first name sort because family. diff last names are shrug
        this.family = [...rest];
        if (primary) {
          this.family.unshift(primary);
        }
      }
    },
    async getBalanceData() {
      let patientId;
      if (this.isAdminUser) {
        patientId = this.$route.query.patientId;
      }

      const response = await DataService.getPatientAccountBalance(patientId);
      if (response.status === 200) {
        this.balanceData = response.data;
      } else {
        this.$root.$emit('raise_alert', { color: 'error', text: 'An error occurred while fetching balance information.' });
      }
    },
    async getBillingBreakdown() {
      let patientId;
      if (this.isAdminUser) {
        patientId = this.$route.query.patientId;
      }

      const response = await DataService.getPatientAccountBreakdown(patientId);
      if (response.status === 200) {
        this.breakdownByFamilyMember = response.data;
      } else {
        this.noPaymentInfoFound = true;
        this.$root.$emit('raise_alert', { color: 'error', text: 'An error occurred while fetching account breakdown information.' });
      }
    },
    showAppointmentDetails(appt) {
      this.selectedAppointment = appt;
      this.showAppointmentDetailDialog = true;
    },
  },
  async mounted() {
    this.loading = true;
    this.isAdminUser = DataService.getRole() !== 'patient';

    await this.getPatientFamily();
    await Promise.all([
      this.getBalanceData(),
      this.getBillingBreakdown(),
    ]);

    this.loading = false;
  },
};
</script>

<style lang="scss">
@import '@/scss/variables.scss';
@import '@/scss/mixins/_mobile-nav-spacing.scss';

// Component-specific color variables
$bill-pay-bg-color: white;
$bill-pay-border-color: #e2e8f0;
$page-title-color: #1f2937;
$patient-card-bg-color: white;
$patient-card-border-color: #e2e8f0;
$patient-card-hover-color: #f8fafc;
$patient-name-color: #1f2937;
$patient-status-color: #6b7280;
$balance-amount-color: #1f2937;
$patient-amount-color: #f59e0b;
$section-title-color: #1f2937;
$table-header-bg-color: #f8fafc;
$table-header-text-color: #374151;
$table-row-border-color: #f1f5f9;
$table-row-hover-color: #f8fafc;
$table-cell-color: #374151;
$broken-appointment-color: #6b7280;
$pending-indicator-color: #f59e0b;
$no-data-color: #6b7280;
$summary-note-color: #6b7280;
$balance-label-color: #374151;
$dialog-bg-color: white;
$dialog-title-color: #1f2937;
$dialog-content-color: #374151;
$detail-title-color: #374151;
$detail-text-color: #6b7280;
$receipt-message-color: #374151;

.bill-pay {
  @include mobile-nav-spacing;
  
  .bill-pay-container {
    padding: 16px;
  }
  
  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    
    .loading-spinner {
      display: block;
    }
  }
  
  .family-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 16px;
    padding: 0px;
  }
  
  .patient-card {
    background: $patient-card-bg-color;
    border: 1px solid $patient-card-border-color;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: $shadow-md;
    margin-bottom: 4px;
    
    .patient-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 24px;
      cursor: pointer;
      transition: background-color 0.2s ease;
      background: $patient-card-bg-color;
      
      &:hover {
        background: #f1f5f9;
      }
      
      @media (max-width: $breakpoint-mobile) {
        padding: 16px 20px;
      }
      
      .patient-info {
        flex: 1;
        
        .patient-name {
          font-size: 18px;
          font-weight: 600;
          color: $patient-name-color;
          margin: 0;
          
          @media (max-width: $breakpoint-mobile) {
            font-size: 16px;
          }
          
          .patient-status {
            font-size: 14px;
            font-weight: 400;
            color: $patient-status-color;
          }
        }
      }
      
      .patient-balance {
        display: flex;
        align-items: center;
        gap: 12px;
        
        .balance-amount {
          font-size: 18px;
          font-weight: 700;
          color: $balance-amount-color;
          
          @media (max-width: $breakpoint-mobile) {
            font-size: 16px;
          }
        }
        
        .expand-icon {
          transition: transform 0.2s ease;
          color: $patient-status-color;
          
          &.expanded {
            transform: rotate(180deg);
          }
        }
      }
    }
    
    .patient-details {
      border-top: 1px solid $patient-card-border-color;
      padding: 24px;
      
      @media (max-width: $breakpoint-mobile) {
        padding: 20px;
      }
      
      .no-data {
        color: $no-data-color;
        font-style: italic;
        text-align: center;
        padding: 20px;
      }
      
      .patient-breakdown {
        display: flex;
        flex-direction: column;
        gap: 32px;
        
        @media (max-width: $breakpoint-mobile) {
          gap: 24px;
        }
      }
      
      .section-title {
        font-size: 18px;
        font-weight: 600;
        color: $section-title-color;
        margin: 0 0 16px 0;
        
        @media (max-width: $breakpoint-mobile) {
          font-size: 16px;
        }
      }
      
      .appointments-table,
      .payments-table {
        border: 1px solid $table-row-border-color;
        border-radius: 8px;
        overflow: hidden;
        background: white;
        
        .table-header {
          display: grid;
          background: $table-header-bg-color;
          padding: 16px;
          font-weight: 600;
          font-size: 14px;
          color: $table-header-text-color;
          border-bottom: 1px solid $table-row-border-color;
        }
        
        .table-row {
          display: grid;
          padding: 16px;
          border-bottom: 1px solid $table-row-border-color;
          cursor: pointer;
          transition: background-color 0.2s ease;
          background: white;
          
          &.payment-row {
            cursor: default;
          }
          
          &:hover {
            background: $table-row-hover-color;
          }
          
          &:last-child {
            border-bottom: none;
          }
        }
        
        .table-cell {
          font-size: 15px;
          color: $table-cell-color;
          display: flex;
          align-items: center;
          font-weight: 500;
          word-break: break-word;
          hyphens: auto;
          
          &.patient-amount {
            color: $patient-amount-color;
            font-weight: 600;
          }
          
          .value-with-indicator {
            color: inherit;
            
            &.has-pending:after {
              content: '*';
              color: #dc2626;
              font-weight: 600;
              margin-left: 4px;
            }
          }
        }
        
        .header-cell {
          font-size: 12px;
          font-weight: 600;
          color: $table-header-text-color;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          line-height: 1.2;
          word-break: break-word;
          hyphens: auto;
        }
      }
      
      .appointments-table {
        .table-header,
        .table-row {
          grid-template-columns: 90px 1fr 80px 80px 80px 90px;
          gap: 12px;
          
          @media (max-width: $breakpoint-tablet) {
            grid-template-columns: 1fr;
            gap: 8px;
          }
        }
        
        @media (max-width: $breakpoint-tablet) {
          .header-cell {
            display: none;
          }
          
          .table-cell {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 0;
            
            &:before {
              content: attr(data-label);
              font-weight: 600;
              color: $table-header-text-color;
              flex-shrink: 0;
              margin-right: 12px;
            }
            
            &.description-col:before {
              margin-right: 16px;
            }
            
            &.patient-amount {
              color: $patient-amount-color;
              font-weight: 600;
            }
            
            &.has-pending:after {
              content: '*';
              color: #dc2626;
              font-weight: 600;
              margin-left: 4px;
            }
          }
        }
      }
      
      .payments-table {
        .table-header,
        .table-row {
          grid-template-columns: 100px 1fr 120px 80px;
          gap: 12px;
          
          @media (max-width: $breakpoint-tablet) {
            grid-template-columns: 1fr;
            gap: 8px;
          }
        }
        
        @media (max-width: $breakpoint-tablet) {
          .header-cell {
            display: none;
          }
          
          .table-cell {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 0;
            
            &:before {
              content: attr(data-label);
              font-weight: 600;
              color: $table-header-text-color;
              flex-shrink: 0;
            }
          }
        }
      }
      
      .empty-state {
        text-align: center;
        padding: 20px 20px;
        color: $no-data-color;
        font-style: italic;
        
        .empty-state-message {
          font-size: 14px;
        }
      }
      
      .broken-appointment {
        color: $broken-appointment-color;
        font-style: italic;
      }

    }
  }
  
  .account-summary {
    background: $bill-pay-bg-color;
    border: 1px solid $bill-pay-border-color;
    border-radius: 12px;
    padding: 20px 24px;
    margin-bottom: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: $shadow-sm;
    
    @media (max-width: $breakpoint-mobile) {
      flex-direction: column;
      gap: 12px;
      align-items: stretch;
      padding: 16px 20px;
    }
    
    .summary-note {
      color: $summary-note-color;
      font-size: 14px;
      margin: 0;
      
      @media (max-width: $breakpoint-mobile) {
        text-align: right;
      }
    }
    
    .total-balance {
      display: flex;
      align-items: center;
      gap: 12px;
      justify-content: flex-end;
      
      @media (max-width: $breakpoint-mobile) {
        justify-content: flex-end;
        width: 100%;
      }
      
      .balance-label {
        font-weight: 600;
        color: $balance-label-color;
        font-size: 16px;
      }
      
      .balance-amount {
        font-size: 18px;
        font-weight: 700;
        color: $balance-amount-color;
      }
    }
  }
  
  .payment-actions {
    display: flex;
    justify-content: flex-end;
    
    @media (max-width: $breakpoint-mobile) {
      justify-content: stretch;
    }
    
    .payment-btn {
      @media (max-width: $breakpoint-mobile) {
        width: 100%;
      }
      
      .v-icon {
        margin-right: 8px;
      }
    }
  }
  
  // Mobile responsive modal
  .v-dialog {
    @media (max-width: $breakpoint-mobile) {
      margin: 16px;
      
      .modern-modal {
        max-width: calc(100vw - 32px) !important;
        width: 100% !important;
        
        .modal-content {
          padding: 16px !important;
        }
        
        .modal-title {
          padding: 16px 16px 8px 16px !important;
          font-size: 16px !important;
        }
        
        .modal-actions {
          padding: 16px !important;
        }
      }
    }
  }
}

// Appointment details modal styles (outside .bill-pay scope)
.appointment-details {
  .detail-section {
    margin-bottom: 24px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .detail-title {
      font-size: 16px;
      font-weight: 600;
      color: #374151;
      margin: 0 0 12px 0;
      
      @media (max-width: $breakpoint-mobile) {
        font-size: 14px;
      }
    }
    
    .detail-text {
      color: #6b7280;
      line-height: 1.5;
      font-size: 15px;
      font-weight: 500;
      word-break: break-word;
      hyphens: auto;
      
      @media (max-width: $breakpoint-mobile) {
        font-size: 14px;
      }
    }
    
    .info-card {
      background: #f8fafc;
      padding: 16px;
      border-radius: 8px;
      margin-bottom: 12px;
      border: 1px solid #e2e8f0;
      
      @media (max-width: $breakpoint-mobile) {
        padding: 12px;
      }
      
      &:last-child {
        margin-bottom: 0;
      }
      
      .info-row {
        display: flex;
        align-items: center;
        margin-bottom: 8px;
        font-size: 15px;
        line-height: 1.4;
        
        @media (max-width: $breakpoint-mobile) {
          font-size: 14px;
          flex-direction: column;
          align-items: flex-start;
          gap: 4px;
        }
        
        &:last-child {
          margin-bottom: 0;
        }
        
        .info-label {
          color: #374151;
          font-weight: 600;
          margin-right: 8px;
          min-width: 140px;
          
          @media (max-width: $breakpoint-mobile) {
            min-width: auto;
            margin-right: 0;
          }
        }
        
        .status-badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          
          &.status-complete {
            background: #d1fae5;
            color: #065f46;
          }
          
          &.status-pending {
            background: #fef3c7;
            color: #92400e;
          }
        }
      }
    }
  }
}
</style>
